/**
 * PWA Install Handler
 * Manages the installation prompt and UI for Progressive Web App
 */

class PWAInstaller {
  constructor() {
    this.deferredPrompt = null;
    this.installButton = null;
    this.isInstalled = false;
    this.debugMode = true; // Enable debugging
    this.init();
  }

  log(message, ...args) {
    if (this.debugMode) {
    }
  }

  error(message, ...args) {
  }

  init() {
    this.log('Initializing PWA Installer...');
    
    // Check browser support
    if (!('serviceWorker' in navigator)) {
      this.error('Service Worker not supported in this browser');
      return;
    }
    
    this.log('Service Worker API available');
    
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
      this.isInstalled = true;
      this.log('App is already installed');
      return;
    }

    setTimeout(() => {
    this.showInstallPromo();
  }, 1000);
    
    this.log('App not installed yet, waiting for beforeinstallprompt event...');

    // Listen for the beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (e) => {
      this.log('✅ beforeinstallprompt event fired! Install criteria met!');
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Store the event so it can be triggered later
      this.deferredPrompt = e;
      this.log('Install prompt stored, showing UI...');
      
      try {
        window.dispatchEvent(new CustomEvent('pwa:deferredpromptready'));
      } catch (eventError) {
        this.error('Failed to dispatch deferred prompt ready event', eventError);
      }
      
      // Show the install button
      this.showInstallPromo();
    });

    // Listen for the app installed event
    window.addEventListener('appinstalled', () => {
      this.log('✅ App installed successfully!');
      this.isInstalled = true;
      this.hideInstallPromo();
      this.deferredPrompt = null;
      
      // Track installation (if analytics is available)
      if (window.gtag) {
        window.gtag('event', 'pwa_installed', {
          event_category: 'PWA',
          event_label: 'Install Successful'
        });
      }
    });

    // Check if iOS
    this.checkIOSInstallPrompt();
    
    // Check PWA requirements after a delay
    setTimeout(() => this.checkPWARequirements(), 2000);
  }

  async checkPWARequirements() {
    this.log('Checking PWA requirements...');
    
    // Check HTTPS
    const isHttps = location.protocol === 'https:' || location.hostname === 'localhost';
    this.log(`HTTPS: ${isHttps ? '✅' : '❌'} (${location.protocol})`);
    
    // Check manifest
    const manifestLink = document.querySelector('link[rel="manifest"]');
    this.log(`Manifest link: ${manifestLink ? '✅' : '❌'}`, manifestLink?.href);
    
    // Check service worker
    if ('serviceWorker' in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        this.log(`Service Worker registrations: ${registrations.length > 0 ? '✅' : '❌'}`, registrations);
        
        if (registrations.length > 0) {
          const sw = registrations[0];
          this.log(`Service Worker state: ${sw.active ? 'active ✅' : sw.installing ? 'installing ⏳' : 'waiting ⏳'}`);
          
          // If service worker is installing, wait for it to become active
          if (sw.installing || sw.waiting) {
            this.log('Waiting for service worker to activate...');
            await navigator.serviceWorker.ready;
            this.log('✅ Service Worker is now active!');
          }
        } else {
          this.error('❌ No service worker registered! This is the main issue.');
          this.log('Checking if service worker script is present...');
          
          // Check if SW registration is being attempted
          setTimeout(() => this.recheckServiceWorker(), 3000);
        }
      } catch (error) {
        this.error('Error checking service worker:', error);
      }
    }
    
    // Check for beforeinstallprompt
    if (!this.deferredPrompt) {
      this.log('⏳ Waiting for beforeinstallprompt event...');
      this.log('Requirements checklist:');
      this.log(`  1. HTTPS: ${isHttps ? '✅' : '❌'}`);
      this.log(`  2. Manifest: ${manifestLink ? '✅' : '❌'}`);
      this.log('  3. Service Worker: Check above');
      this.log('  4. Not already installed: ❓');
      this.log('  5. Chrome/Edge browser: ❓');
    }
  }

  async recheckServiceWorker() {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      if (registrations.length > 0) {
        this.log('✅ Service Worker now registered! Waiting for beforeinstallprompt...');
        await navigator.serviceWorker.ready;
        this.log('✅ Service Worker is active and ready!');
      } else {
        this.error('❌ Service Worker still not registered after 3 seconds');
      }
    }
  }

  checkIOSInstallPrompt() {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isInStandaloneMode = ('standalone' in window.navigator) && (window.navigator.standalone);

    if (isIOS && !isInStandaloneMode) {
      // Show iOS-specific install instructions
      this.showIOSInstallInstructions();
    }
  }

  showIOSInstallInstructions() {
    // Create iOS install banner (optional)
    const iosBanner = document.createElement('div');
    iosBanner.id = 'ios-install-banner';
    iosBanner.className = 'pwa-ios-banner';
    iosBanner.innerHTML = `
    `;

    // Check if banner was dismissed
    if (localStorage.getItem('pwa_ios_banner_dismissed') === 'true') {
      return;
    }

    document.body.appendChild(iosBanner);

    // Close button handler
    const closeBtn = iosBanner.querySelector('.pwa-ios-banner__close');
    closeBtn.addEventListener('click', () => {
      iosBanner.remove();
      localStorage.setItem('pwa_ios_banner_dismissed', 'true');
    });
  }

  showInstallPromo() {
    if (!this.isMobileDevice()) {
      return;
    }

    const dismissedTime = localStorage.getItem('pwa_install_dismissed');
    if (dismissedTime) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }

    this.createInstallUI();
  }

  isMobileDevice() {
    const uaMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const coarsePointer = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    const narrowViewport = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
    return uaMobile || (coarsePointer && narrowViewport);
  }

  createInstallUI() {
    // Check if UI already exists
    if (document.getElementById('pwa-install-container')) {
      return;
    }

    // Get logo URL dynamically
    const logoUrl = this.getPWALogoUrl();

    // Create install banner
    const installContainer = document.createElement('div');
    installContainer.id = 'pwa-install-container';
    installContainer.className = 'pwa-install-banner';
    installContainer.innerHTML = `
      <div class="pwa-install-banner__inner">
      
        <div class="pwa-install-banner__content">
        <button type="button" id="pwa-dismiss-btn" class="pwa-dismiss-btn" aria-label="Dismiss">&times;</button>
          <div class="pwa-install-banner__icon">
            <img src="${logoUrl}" alt="PEP logo">
          </div>
          <div class="pwa-install-banner__text">
            <span>Add the app to your home screen for faster shopping.</span>
          </div>
          <div class="pwa-install-banner__actions">
          <button type="button" id="pwa-install-btn" class="pwa-install-btn">Install</button>
          
        </div>
        </div>
        
      </div>
    `;

    document.body.appendChild(installContainer);

    this.installButton = document.getElementById('pwa-install-btn');
    const dismissBtn = document.getElementById('pwa-dismiss-btn');

    if (this.installButton) {
      this.installButton.addEventListener('click', () => this.promptInstall());
    }
    if (dismissBtn) {
      dismissBtn.addEventListener('click', () => this.dismissInstallPromo());
    }

    // Show banner with animation
    setTimeout(() => {
      installContainer.classList.add('pwa-install-banner--visible');
    }, 500);
  }

  async promptInstall() {
    if (!this.deferredPrompt) {
      return;
    }

    // Directly show Chrome's native install prompt (skip custom modal)
    try {
      // Show the native install prompt
      this.deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      const { outcome } = await this.deferredPrompt.userChoice;

      // Track the outcome
      if (window.gtag) {
        window.gtag('event', 'pwa_install_prompt', {
          event_category: 'PWA',
          event_label: outcome
        });
      }

      if (outcome === 'accepted') {
        this.hideInstallPromo();
      } else {
      }

      // Clear the deferred prompt
      this.deferredPrompt = null;
    } catch (error) {
    }
  }

  showInstallModal() {
    // Get icon URL from branding image or construct from manifest
    let iconUrl = this.getAppIcon();
    
    // Try to get icon from the branding image in the fake pop-up (if it exists)
    const brandingImg = document.querySelector('.pwa-install-prompt__branding img');
    if (brandingImg && brandingImg.src) {
      iconUrl = brandingImg.src;
    } else {
      // Try to get from manifest
      const manifestLink = document.querySelector('link[rel="manifest"]');
      if (manifestLink) {
        // Construct icon URL from manifest path
        const manifestPath = manifestLink.href;
        const manifestBase = manifestPath.substring(0, manifestPath.lastIndexOf('/'));
        // Use logo-pwa.svg from assets (same as manifest uses)
        iconUrl = manifestBase + '/logo-pwa.svg';
      }
    }
    
    // Create modal overlay
    const modal = document.createElement('div');
    modal.id = 'pwa-install-modal';
    modal.className = 'pwa-modal';
    modal.innerHTML = `
      <div class="pwa-modal__overlay"></div>
      <div class="pwa-modal__content">
        <div class="pwa-modal__header">
          <h2>Install app</h2>
        </div>
        <div class="pwa-modal__body">
          <div class="pwa-modal__app-info">
            <div class="pwa-modal__app-icon-wrapper">
              <img src="${iconUrl}" alt="App Icon" class="pwa-modal__app-icon" onerror="this.src='${this.getAppIcon()}'">
            </div>
            <div class="pwa-modal__app-details">
              <h3>${this.getAppName()}</h3>
              <p>${window.location.hostname}</p>
            </div>
          </div>
        </div>
        <div class="pwa-modal__footer">
          <button class="pwa-modal__button pwa-modal__button--secondary" id="pwa-modal-cancel">
            Cancel
          </button>
          <button class="pwa-modal__button pwa-modal__button--primary" id="pwa-modal-install">
            Install
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Show modal with animation
    setTimeout(() => {
      modal.classList.add('pwa-modal--visible');
    }, 10);

    // Add event listeners
    const cancelBtn = document.getElementById('pwa-modal-cancel');
    const installBtn = document.getElementById('pwa-modal-install');
    const overlay = modal.querySelector('.pwa-modal__overlay');

    const closeModal = () => {
      modal.classList.remove('pwa-modal--visible');
      setTimeout(() => modal.remove(), 300);
    };

    cancelBtn.addEventListener('click', closeModal);
    overlay.addEventListener('click', closeModal);

    installBtn.addEventListener('click', async () => {
      // Show the install prompt
      this.deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      const { outcome } = await this.deferredPrompt.userChoice;

      // Track the outcome
      if (window.gtag) {
        window.gtag('event', 'pwa_install_prompt', {
          event_category: 'PWA',
          event_label: outcome
        });
      }

      if (outcome === 'accepted') {
        this.hideInstallPromo();
      } else {
      }

      // Clear the deferred prompt
      this.deferredPrompt = null;
      closeModal();
    });
  }

  dismissInstallPromo() {
    const banner = document.getElementById('pwa-install-container');
    if (banner) {
      banner.classList.remove('pwa-install-banner--visible');
      setTimeout(() => banner.remove(), 300);
    }
    
    // Store dismissal time
    localStorage.setItem('pwa_install_dismissed', Date.now().toString());
    
    // Track dismissal
    if (window.gtag) {
      window.gtag('event', 'pwa_install_dismissed', {
        event_category: 'PWA',
        event_label: 'Banner Dismissed'
      });
    }
  }

  hideInstallPromo() {
    const banner = document.getElementById('pwa-install-container');
    if (banner) {
      banner.classList.remove('pwa-install-banner--visible');
      setTimeout(() => banner.remove(), 300);
    }
  }

  getAppName() {
    return document.querySelector('meta[property="og:site_name"]')?.content || 
           document.title || 
           'Shop';
  }

  getAppIcon() {
    return document.querySelector('link[rel="icon"]')?.href ||
           document.querySelector('link[rel="shortcut icon"]')?.href ||
           '/favicon.ico';
  }

  getPWALogoUrl() {
    // Try to get from manifest first
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      const manifestPath = manifestLink.href;
      const manifestBase = manifestPath.substring(0, manifestPath.lastIndexOf('/'));
      return manifestBase + '/logo-pwa.svg';
    }
    // Fallback to favicon
    return this.getAppIcon();
  }
}

// Initialize PWA installer when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.pwaInstaller = new PWAInstaller();
  });
} else {
  window.pwaInstaller = new PWAInstaller();
}

// Note: Service worker registration is handled by pwa-service-worker-inline.liquid snippet

